import { concat } from '@ember/helper';
import { or, eq } from 'tio-ui/utilities';
import { t } from 'ember-intl';
import { on } from '@ember/modifier';
import { tracked } from '@glimmer/tracking';
import Chip from 'tio-reimagined/components/chip';
import Popover from 'tio-reimagined/components/popover';
import safeFormatDate from 'tio-common/helpers/safe-format-date';
import Component from '@glimmer/component';
import type PslfFormModel from 'tio-common/models/pslf-form';
import {
  PhPaperPlaneTilt,
  PhUserGear,
  PhEyeSlash,
  PhDotsThreeVertical,
  PhEye,
  PhUserCheck,
} from 'ember-phosphor-icons';
import { fn } from '@ember/helper';

interface S {
  Args: {
    doFormAction?: (action: string, model: PslfFormModel) => void;
    isLoading: boolean;
    isResending?: boolean;
    model: PslfFormModel;
    onPreview: (model: PslfFormModel) => void;
    cellClass?: string;
  };
}

export default class PslfMyDocumentsMyFormsTableRowComponent extends Component<S> {
  @tracked isMenuOpen = false;

  toggleMenu = () => {
    this.isMenuOpen = !this.isMenuOpen;
  };

  closeMenu = () => {
    this.isMenuOpen = false;
  };

  handleMenuAction = (action: string) => {
    this.isMenuOpen = false;
    if (action === 'preview') {
      this.args.onPreview?.(this.args.model);
    } else {
      this.args.doFormAction?.(action, this.args.model);
    }
  };

  <template>
    <tr>
      {{#let @model.employerCompletedAt as |date|}}
        <td class={{@cellClass}} title={{date}}>
          {{#if date}}
            {{safeFormatDate date month="short" day="2-digit" year="numeric"}}
          {{else if (or @model.isPrintAndMail @model.isPriorEmployerClosed)}}
            {{t "pslf.table.not_applicable"}}
          {{else if @model.isAwaitingSignature}}
            {{t "pslf.table.pending"}}
          {{/if}}
        </td>
      {{/let}}

      {{#let @model.borrowerCompletedAt as |date|}}
        <td class={{@cellClass}} title={{date}}>
          {{#if @model.isOboForm}}
            <div class="flex flex-col gap-1">
              <Popover @triggerMode="hover">
                <:trigger as |pop|>
                  <Chip
                    @intent="primary"
                    @size="sm"
                    {{pop.register}}
                    {{pop.hoverHandlers}}
                    tabindex="0"
                    class="cursor-help w-fit"
                  >
                    <PhUserCheck @size="12" />
                    {{t "pslf.document_repo.submitted_on_your_behalf"}}
                  </Chip>
                </:trigger>
                <:body>
                  <div class="p-3 max-w-xs">
                    {{t "pslf.document_repo.submitted_on_your_behalf_tooltip"}}
                  </div>
                </:body>
              </Popover>
              {{#if date}}
                <span class="text-sm text-gray-500">
                  {{safeFormatDate date month="short" day="2-digit" year="numeric"}}
                </span>
              {{/if}}
            </div>
          {{else if date}}
            {{safeFormatDate date month="short" day="2-digit" year="numeric"}}
          {{else}}
            {{t "pslf.table.pending"}}
          {{/if}}
        </td>
      {{/let}}

      <td class={{@cellClass}}>
        {{#if
          (or
            (eq @model.status "canceled")
            (eq @model.status "cancelled")
            (eq @model.status "submitted")
          )
        }}
          <Popover @triggerMode="hover">
            <:trigger as |pop|>
              <span {{pop.register}} {{pop.hoverHandlers}}>
                {{t (concat "common.pslf.status." @model.status)}}
              </span>
            </:trigger>
            <:body>
              <div class="p-4">
                {{t (concat "pslf.status_tippy." @model.status) htmlSafe=true}}
              </div>
            </:body>
          </Popover>
        {{else}}
          {{t (concat "common.pslf.status." @model.status)}}
        {{/if}}
      </td>
      <td class={{@cellClass}}>
        {{@model.approverDisplayEmail}}
      </td>
      <td class={{@cellClass}}>
        <div class="truncate">
          {{t (concat "common.pslf.form-type." @model.formTypeDisplayKey)}}
          <br />
          {{@model.employerDisplayName}}
        </div>
      </td>
      <td class={{@cellClass}}>
        <div class="flex justify-end">
          <Popover
            @placement="left"
            @isOpen={{this.isMenuOpen}}
            @onClose={{this.closeMenu}}
            @maxWidth={{200}}
          >
            <:trigger as |p|>
              <button
                type="button"
                class="p-1 rounded border border-current text-primary hover:bg-primary/10"
                {{p.register}}
                {{on "click" this.toggleMenu}}
                data-test-actions-trigger={{@model.id}}
                aria-label={{t "pslf.document_repo.row_actions"}}
                aria-haspopup="menu"
                aria-expanded={{this.isMenuOpen}}
              >
                <PhDotsThreeVertical @size="24" />
              </button>
            </:trigger>
            <:body>
              <div class="flex flex-col py-1">
                {{#if @model.hasPdfPreview}}
                  <button
                    type="button"
                    class="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
                    {{on "click" (fn this.handleMenuAction "preview")}}
                    data-test-preview={{@model.id}}
                  >
                    <PhEye @size="16" />
                    {{t "pslf.document_repo.preview"}}
                  </button>
                {{/if}}
                {{#if @model.canResendEmail}}
                  <button
                    type="button"
                    class="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
                    {{on "click" (fn this.handleMenuAction "resend")}}
                    data-test-resend={{@model.id}}
                  >
                    <PhPaperPlaneTilt @size="16" />
                    {{t "pslf.document_repo.resend"}}
                  </button>
                {{/if}}
                {{#if @model.canChangeApprover}}
                  <button
                    type="button"
                    class="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
                    {{on "click" (fn this.handleMenuAction "changeApprover")}}
                    data-test-change-approver={{@model.id}}
                  >
                    <PhUserGear @size="16" />
                    {{t "pslf.document_repo.change_approver"}}
                  </button>
                {{/if}}
                {{#if @model.canCancel}}
                  <button
                    type="button"
                    class="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-600 hover:bg-gray-100 rounded"
                    {{on "click" (fn this.handleMenuAction "cancel")}}
                    data-test-cancel={{@model.id}}
                  >
                    <PhEyeSlash @size="16" />
                    {{t "disable"}}
                  </button>
                {{/if}}
              </div>
            </:body>
          </Popover>
        </div>
      </td>
    </tr>
  </template>
}
